import {
  type CSSProperties,
  type ReactNode,
  memo,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Button,
  InlineLoading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { ChevronDown, ChevronRight } from '@carbon/icons-react';
import classNames from 'classnames';
import { FormattedCount } from 'components/formatted-count';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { useInfiniteScroll } from 'lib/hooks/useInfiniteScroll';
import { groupFactorsQueryOptions, useSlotRows } from '../../hooks';
import {
  FACTOR_GROUPS,
  dollarsDeltaSign,
  formatDollars,
  formatPercent,
  formatSignedDollars,
  percentDeltaSign,
  rescatEmptyMessage,
  rescatOption,
  toSlotRows,
  toSummaryRows,
} from '../../utils';
import { isSlotCoverageKey } from '../../slot-reveal-utils';
import type { RunResponse, SlotSummaryRow, SummaryRow } from '../../types';
import styles from './summary-table.module.scss';

/** The numeric columns, in order — the table's header, and each slot list's (screen-reader) one. */
const NUMERIC_HEADERS = ['Origin NPV', 'Target NPV', '$ Change', '% Change'];

/** Columns in the table (the driver + the numeric ones) — the full-width rows' colSpan. */
const COLUMN_COUNT = 1 + NUMERIC_HEADERS.length;

/** The overview rows that expand to their factors — the factor groups. */
const FACTOR_GROUP_KEYS = new Set(FACTOR_GROUPS.map((group) => group.key));

/** Skeleton rows while the run calculates — one per factor group, the shape the table settles into. */
const SKELETON_ROWS = FACTOR_GROUPS.length;

/** A row's place in the hierarchy, which drives its styling. */
type RowLevel = 'group' | 'factor' | 'total';

/** The per-level cell class (factor and slot rows use the base styling). */
const LEVEL_CELL_CLASS: Record<RowLevel, string | undefined> = {
  group: styles.groupCell,
  factor: undefined,
  total: styles.totalCell,
};

/**
 * What an overview row expands to: a drillable factor group to its factors, a drillable Slots
 * Removed / Slots Added row straight to its slots. Anything else (Unexplained, the Total) is a leaf.
 *
 * @param {SummaryRow} row - An overview row.
 * @return {'factors' | 'slots' | null} What the row opens onto, or null for a leaf.
 */
function overviewChildren(row: SummaryRow): 'factors' | 'slots' | null {
  if (!row.drillable) return null;
  if (FACTOR_GROUP_KEYS.has(row.key)) return 'factors';
  return isSlotCoverageKey(row.key) ? 'slots' : null;
}

/**
 * The open rows with one row flipped open or closed. The rows beneath it keep their own state, so
 * re-opening a row restores what was open under it.
 *
 * @param {Set<string>} paths - The open rows' paths.
 * @param {string} path - The row to flip.
 * @return {Set<string>} A new set.
 */
function toggled(paths: Set<string>, path: string): Set<string> {
  const next = new Set(paths);
  if (next.has(path)) next.delete(path);
  else next.add(path);
  return next;
}

/**
 * Resolves a row's hierarchy level: the Total, a top-level row, or a row nested under one.
 *
 * @param {SummaryRow | SlotSummaryRow} row - The row.
 * @param {number} depth - Its nesting depth (0 = the overview).
 * @return {RowLevel} The row's level.
 */
function rowLevel(row: SummaryRow | SlotSummaryRow, depth: number): RowLevel {
  if (row.isTotal) return 'total';
  return depth === 0 ? 'group' : 'factor';
}

/**
 * The column widths, shared by the table and each slot list nested in it: both lay out fixed, so
 * a nested list's columns sit under the table's. The driver column takes what the numeric ones
 * leave.
 *
 * @return {JSX.Element} The column group.
 */
function ColumnWidths() {
  return (
    <colgroup>
      <col />
      {NUMERIC_HEADERS.map((header) => (
        <col key={header} className={styles.numericColumn} />
      ))}
    </colgroup>
  );
}

/**
 * One data row: the driver name (indented by depth, with an expand chevron when the row
 * expands) and its Origin/Target NPV, $ Change, and % Change — each amount in the unit that fits
 * its size. Each change is colored by its sign as displayed here (one that reads as zero stays
 * neutral). The waterfall reads a change too small for millions the same way (formatBarChange),
 * so a +$30K shows, and is colored, as a change in both. An expandable row toggles on a
 * click anywhere in it; the chevron button is its keyboard / screen-reader control. Memoized: a
 * loaded slot row keeps its identity (see toSlotRows), so a new page renders only its own rows.
 *
 * @param {number} depth - Nesting depth (0 overview, 1 factor or slot, 2 slot) for indentation.
 * @param {SummaryRow | SlotSummaryRow} row - The row's values.
 * @param {boolean} expanded - Whether this (expandable) row is open.
 * @param {Function | null} onToggle - Expand/collapse handler, or null for a leaf.
 * @param {string} [expanderId] - The expander button's id, so a retry beneath the row can hold
 *   keyboard focus on it.
 * @return {JSX.Element} The table row.
 */
const ValueRow = memo(
  ({
    depth,
    row,
    expanded,
    onToggle,
    expanderId,
  }: {
    depth: number;
    row: SummaryRow | SlotSummaryRow;
    expanded: boolean;
    onToggle: (() => void) | null;
    expanderId?: string;
  }) => {
    const levelCell = LEVEL_CELL_CLASS[rowLevel(row, depth)];
    return (
      <TableRow
        className={classNames({ [styles.drillableRow]: !!onToggle })}
        onClick={onToggle ?? undefined}
      >
        <TableCell className={levelCell}>
          <div className={classNames(styles.driver, styles[`depth${depth}`])}>
            {onToggle ? (
              // A native button, like components/data-table's expander — an icon-only Carbon
              // Button would put a tooltip on every row.
              <button
                id={expanderId}
                type="button"
                className={styles.expandButton}
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.label}`}
                aria-expanded={expanded}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle();
                }}
              >
                {expanded ? <ChevronDown /> : <ChevronRight />}
              </button>
            ) : (
              <span className={styles.chevronSpacer} />
            )}
            <span>{row.label}</span>
          </div>
        </TableCell>
        <TableCell className={levelCell}>
          <span className={styles.numeric}>{formatDollars(row.origin)}</span>
        </TableCell>
        <TableCell className={levelCell}>
          <span className={styles.numeric}>{formatDollars(row.target)}</span>
        </TableCell>
        <TableCell className={levelCell}>
          <span
            className={classNames(
              styles.numeric,
              styles[dollarsDeltaSign(row.delta)]
            )}
          >
            {formatSignedDollars(row.delta)}
          </span>
        </TableCell>
        <TableCell className={levelCell}>
          <span
            className={classNames(
              styles.numeric,
              styles[percentDeltaSign(row.deltaPct)]
            )}
          >
            {formatPercent(row.deltaPct)}
          </span>
        </TableCell>
      </TableRow>
    );
  }
);
ValueRow.displayName = 'ValueRow';

/**
 * A full-width status row (loading / error / empty) shown under an expanded parent.
 *
 * @param {number} depth - Nesting depth for the message's indentation.
 * @param {ReactNode} children - The status content.
 * @return {JSX.Element} The table row.
 */
function StatusRow({
  depth,
  children,
}: {
  depth: number;
  children: ReactNode;
}) {
  return (
    <TableRow>
      <TableCell colSpan={COLUMN_COUNT}>
        <div className={classNames(styles.status, styles[`depth${depth}`])}>
          {children}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * A load that failed, announced to screen readers, with a way to retry it.
 *
 * @param {string} message - What couldn't load.
 * @param {Function} onRetry - Retries the load.
 * @return {JSX.Element} The message and its Try again button.
 */
function LoadFailed({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <span className={styles.loadFailed}>
      <span role="alert" className={styles.error}>
        {message}
      </span>
      <Button kind="ghost" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </span>
  );
}

interface SlotRowsSectionProps {
  runId: number;
  /** The factor (or `slots_removed` / `slots_added`) whose slots to list. */
  factorKey: string;
  /** The reserve category to scope the list to (null = all). */
  rescat: string | null;
  /** The slot rows' nesting depth — 1 under Slots Removed / Added, 2 under a factor. */
  depth: number;
  /** What the list breaks down, e.g. "Costs · CapEx" — its region's accessible name. */
  label: string;
  /** List the slots that didn't change too (the page's "Show $0 slots" switch). */
  showUnchanged: boolean;
}

/**
 * The slots behind an expanded row: every slot, biggest change first, in a scroll area of its own
 * that loads the next page as the user nears its end — the ones that didn't change only
 * when the page shows them, so the caption counts what's listed. Each slot row shows the slot's
 * own NPV before → after the step ("—" on a side the slot isn't priced on).
 *
 * One full-width row holds a nested table whose columns line up under the parent's (see
 * ColumnWidths). The scroll area's scrollbar would narrow it, so its width is measured and taken
 * off the last column. The nested table repeats the column names for screen readers only, and its
 * rows aren't read aloud as they load — only the list's loading and failure states are. A retry
 * keeps keyboard focus in the list. Memoized: its props are all primitives, so the table's other
 * toggles don't re-render an open list.
 *
 * @param {SlotRowsSectionProps} props - The list to show and where it sits.
 * @return {JSX.Element} The table row holding the list.
 */
const SlotRowsSection = memo(
  ({
    runId,
    factorKey,
    rescat,
    depth,
    label,
    showUnchanged,
  }: SlotRowsSectionProps) => {
    const {
      data,
      isError,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      refetch,
    } = useSlotRows(runId, factorKey, rescat, {
      includeUnchanged: showUnchanged,
    });
    const slotRows = useMemo(
      () => (data ? toSlotRows(data.pages) : []),
      [data]
    );
    const totalCount = data?.pages[data.pages.length - 1]?.totalCount ?? 0;
    const captionId = useId();

    // Outlives every state below, so a retry has somewhere to keep keyboard focus.
    const sectionRef = useRef<HTMLDivElement>(null);
    // The scroll area: the loader watches within it, and its scrollbar's width comes off the nested
    // table's last column. The gutter is reserved (stable), so one measurement holds.
    const [scroller, setScroller] = useState<{
      element: HTMLDivElement;
      scrollbarWidth: number;
    } | null>(null);
    /**
     * Keeps the scroll area and its scrollbar's width as the area mounts and unmounts.
     *
     * @param {HTMLDivElement | null} element - The scroll area, or null once it unmounts.
     */
    const scrollerRef = useCallback((element: HTMLDivElement | null) => {
      setScroller(
        element
          ? {
              element,
              scrollbarWidth: element.offsetWidth - element.clientWidth,
            }
          : null
      );
    }, []);

    // A failed page stops the loading until the user retries — the list's end stays in view, so
    // loading on sight would otherwise retry it in a loop.
    const { sentinelRef } = useInfiniteScroll({
      onLoadMore: fetchNextPage,
      hasMore: !!hasNextPage && !isError,
      loading: isFetchingNextPage,
      root: scroller?.element,
    });

    // The list's first page not loaded yet (or failed), or loaded empty — else the list itself.
    let status: ReactNode = null;
    if (!data) {
      status = isError ? (
        <LoadFailed
          message="Couldn't load these slots."
          onRetry={() => {
            sectionRef.current?.focus();
            refetch();
          }}
        />
      ) : (
        <InlineLoading description="Loading slots…" aria-live="polite" />
      );
    } else if (slotRows.length === 0) {
      const scope = rescat ? `${rescatOption(rescat).label} ` : '';
      status = (
        <span className={styles.muted}>
          {/* No hint at the $0 switch: an empty list can't tell a category whose slots all held from
              one with no slots at all, where the switch would show nothing more. */}
          {showUnchanged
            ? `No ${scope}slots to show.`
            : `No ${scope}slots with a change.`}
        </span>
      );
    }

    return (
      <TableRow className={styles.slotListRow}>
        <TableCell colSpan={COLUMN_COUNT} className={styles.slotListCell}>
          <div ref={sectionRef} tabIndex={-1} className={styles.slotSection}>
            {status ? (
              <div
                className={classNames(
                  styles.slotStatus,
                  styles.status,
                  styles[`depth${depth}`]
                )}
              >
                {status}
              </div>
            ) : (
              <>
                <div id={captionId} className={styles.slotCaption}>
                  <div
                    className={classNames(
                      styles.driver,
                      styles[`depth${depth}`]
                    )}
                  >
                    <span className={styles.chevronSpacer} />
                    <span>
                      <FormattedCount value={totalCount} />{' '}
                      {totalCount === 1 ? 'slot' : 'slots'} · sorted by impact
                    </span>
                  </div>
                </div>
                {/* Focusable, so the list scrolls from the keyboard too. */}
                <div
                  ref={scrollerRef}
                  role="region"
                  aria-label={`${label} by slot`}
                  aria-describedby={captionId}
                  tabIndex={0}
                  className={styles.slotScroll}
                  style={
                    {
                      '--slot-list-scrollbar': `${
                        scroller?.scrollbarWidth ?? 0
                      }px`,
                    } as CSSProperties
                  }
                >
                  {/* A plain <table> around Carbon's parts: a Carbon Table would take the outer
                    table's title and description as its own name (from TableContainer). */}
                  <table
                    className={styles.slotTable}
                    aria-rowcount={totalCount + 1}
                  >
                    <ColumnWidths />
                    <TableHead>
                      <TableRow className={styles.hiddenHeadRow}>
                        {['Slot', ...NUMERIC_HEADERS].map((header) => (
                          <TableHeader
                            key={header}
                            className={styles.hiddenHeadCell}
                          >
                            <span className={styles.visuallyHidden}>
                              {header}
                            </span>
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody aria-live="off">
                      {slotRows.map((slotRow) => (
                        <ValueRow
                          key={slotRow.key}
                          depth={depth}
                          row={slotRow}
                          expanded={false}
                          onToggle={null}
                        />
                      ))}
                      {isFetchingNextPage && (
                        <StatusRow depth={depth}>
                          <InlineLoading
                            description="Loading more slots…"
                            aria-live="polite"
                          />
                        </StatusRow>
                      )}
                      {isError && hasNextPage && !isFetchingNextPage && (
                        <StatusRow depth={depth}>
                          <LoadFailed
                            message="Couldn't load more slots."
                            onRetry={() => {
                              scroller?.element.focus();
                              fetchNextPage();
                            }}
                          />
                        </StatusRow>
                      )}
                    </TableBody>
                  </table>
                  {hasNextPage && (
                    <div ref={sentinelRef} className={styles.sentinel} />
                  )}
                </div>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }
);
SlotRowsSection.displayName = 'SlotRowsSection';

interface SummaryTableProps {
  /** The comparison run whose overview (already scoped to `rescat`) to tabulate. */
  run: RunResponse;
  /** The run's reserve category (null = all), i.e. `run.rescat`; scopes every nested fetch. */
  rescat: string | null;
  /**
   * The run is still calculating: keep the framed table and its headers, with skeleton rows in
   * place of any values, so no partial numbers ever show (default false).
   */
  loading?: boolean;
  /** List the slots that didn't change too (default false: they're left out). */
  showUnchanged?: boolean;
}

/**
 * The Step-4 Summary Table: a Factor Group → Factor → Slot view of the NPV change, the tabular
 * twin of the waterfall. Each row shows its Origin/Target NPV, $ Change, and % Change.
 *
 * - A factor group expands to its factors, fetched with the SAME query keys the chart's drill
 *   uses, so a level fetched by either view is shared from cache.
 * - A factor, and the Slots Removed / Slots Added rows, expand to every slot behind them, in a
 *   scroll area of their own that loads as the user scrolls (see SlotRowsSection) — leaving out
 *   the slots that didn't change unless `showUnchanged`, as the chart does.
 *
 * Unlike the chart (one active level), the table can hold many rows open at once. Expansion is
 * its own local state — the caller keys the table by run, so it resets per run — and is kept
 * across reserve-category switches. Collapsing a row keeps what was open beneath it, so
 * re-opening the row restores it (from cache).
 *
 * The body isn't a live region (Carbon's TableBody defaults to one): rows appearing as the user
 * expands and scrolls aren't read aloud — the expand buttons' state, the loading indicators and
 * the failure alerts carry what screen readers need.
 *
 * Built on Carbon's primitive Table rather than components/data-table: that wrapper expands a
 * row into a free-form panel, but this needs child rows aligned to the parent's columns, each
 * level lazy-fetched on expand.
 *
 * @param {SummaryTableProps} props - The run to tabulate, its reserve category, whether it is
 *   still calculating, and whether its slot lists show $0 slots.
 * @return {JSX.Element} The summary table.
 */
export function SummaryTable({
  run,
  rescat,
  loading = false,
  showUnchanged = false,
}: SummaryTableProps) {
  // The open rows, by their path: `g:<key>` for an overview row, `g:<group>/f:<factor>` for a
  // factor under its group.
  const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
  // Prefixes each overview row's expander id (see the group retry below).
  const expanderIdPrefix = useId();
  /**
   * Flips one row open or closed.
   *
   * @param {string} path - The row's path.
   */
  const toggle = (path: string) => {
    setOpenPaths((prev) => toggled(prev, path));
  };

  // The overview rows (one per delta bar, plus the Total). None while the run still calculates.
  const overviewRows = loading
    ? []
    : toSummaryRows(run.waterfall, run.drillableKeys);

  // Lazy per-group fetches, one query per open factor group, keyed identically to the chart's
  // drill hooks (shared cache); results map back to their group by index.
  const openGroupKeys = overviewRows
    .filter(
      (row) =>
        overviewChildren(row) === 'factors' && openPaths.has(`g:${row.key}`)
    )
    .map((row) => row.key);
  const groupResults = useQueries({
    queries: openGroupKeys.map((key) =>
      groupFactorsQueryOptions(run.id, key, rescat)
    ),
  });
  const groupResultByKey = new Map(
    openGroupKeys.map((key, index) => [key, groupResults[index]])
  );

  const rows: ReactNode[] = [];

  overviewRows.forEach((overviewRow) => {
    const path = `g:${overviewRow.key}`;
    const children = overviewChildren(overviewRow);
    const open = children !== null && openPaths.has(path);
    rows.push(
      <ValueRow
        key={path}
        depth={0}
        row={overviewRow}
        expanded={open}
        onToggle={children ? () => toggle(path) : null}
        expanderId={`${expanderIdPrefix}${path}`}
      />
    );
    if (!open) return;

    if (children === 'slots') {
      rows.push(
        <SlotRowsSection
          key={`${path}:slots`}
          runId={run.id}
          factorKey={overviewRow.key}
          rescat={rescat}
          depth={1}
          label={overviewRow.label}
          showUnchanged={showUnchanged}
        />
      );
      return;
    }

    const groupQuery = groupResultByKey.get(overviewRow.key);
    if (!groupQuery || groupQuery.isLoading) {
      rows.push(
        <StatusRow key={`${path}:loading`} depth={1}>
          <InlineLoading description="Loading factors…" aria-live="polite" />
        </StatusRow>
      );
      return;
    }
    // A breakdown that never loaded offers Try again: re-opening the group would refetch it too,
    // but nothing says so. One that fails a later refresh keeps its rows (a finished run's
    // breakdown doesn't change).
    if (groupQuery.isError && !groupQuery.data) {
      rows.push(
        <StatusRow key={`${path}:error`} depth={1}>
          <LoadFailed
            message={`Couldn't load the ${overviewRow.label} breakdown.`}
            onRetry={() => {
              // The group's expander outlives the error, loading and loaded rows, so keyboard
              // focus waits there while they swap.
              document.getElementById(`${expanderIdPrefix}${path}`)?.focus();
              groupQuery.refetch();
            }}
          />
        </StatusRow>
      );
      return;
    }
    // A group drill is anchored at the common-slot subtotal, so its rows are shifted to start
    // at the group row's Origin — they then sit inside the group's own Origin→Target range.
    // A factor the drill marks drillable expands to its slots.
    const factorRows = groupQuery.data
      ? toSummaryRows(
          groupQuery.data.waterfall,
          groupQuery.data.drillableKeys,
          { includeTotal: false, originBase: overviewRow.origin }
        )
      : [];
    if (factorRows.length === 0) {
      rows.push(
        <StatusRow key={`${path}:empty`} depth={1}>
          <span className={styles.muted}>No further detail.</span>
        </StatusRow>
      );
      return;
    }
    factorRows.forEach((factorRow) => {
      const factorPath = `${path}/f:${factorRow.key}`;
      const factorOpen = factorRow.drillable && openPaths.has(factorPath);
      rows.push(
        <ValueRow
          key={factorPath}
          depth={1}
          row={factorRow}
          expanded={factorOpen}
          onToggle={factorRow.drillable ? () => toggle(factorPath) : null}
        />
      );
      if (factorOpen) {
        rows.push(
          <SlotRowsSection
            key={`${factorPath}:slots`}
            runId={run.id}
            factorKey={factorRow.key}
            rescat={rescat}
            depth={2}
            label={`${overviewRow.label} · ${factorRow.label}`}
            showUnchanged={showUnchanged}
          />
        );
      }
    });
  });

  // Nothing to tabulate once the run is done: a reserve category with nothing to show, or no
  // waterfall at all. Keep the framed table with a placeholder rather than a bare header.
  if (!loading && rows.length === 0) {
    rows.push(
      <TableRow key="empty">
        <TableCell colSpan={COLUMN_COUNT}>
          <div className={styles.status}>
            {rescat ? rescatEmptyMessage(rescat) : 'No value drivers to show.'}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableContainer
      title="Value driver summary"
      description="Each row shows the NPV before and after its change — expand a factor group for its factors, and a factor for its slots."
      className={styles.container}
    >
      <Table size="md" aria-busy={loading} className={styles.table}>
        <ColumnWidths />
        <TableHead>
          <TableRow>
            <TableHeader>Factor Group / Factor / Slot</TableHeader>
            {NUMERIC_HEADERS.map((header) => (
              <TableHeader key={header}>
                <span className={styles.numeric}>{header}</span>
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody aria-live="off">
          {loading ? (
            <SkeletonTableRows columns={COLUMN_COUNT} rows={SKELETON_ROWS} />
          ) : (
            rows
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
